import React, { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { Box, Chip, TextField, Tooltip, Typography } from "@mui/material";

import { isEmailValid } from "../../Support/utils";
import { useAccountManagerEmails } from "./hooks";
import { useMeasureTextWidth } from "./utils";

type Props = {
  handleChange: (emails: string[]) => void;
  isLegacyFlow?: boolean;
  platform: string;
  priority: string;
};

function EmailChipsInput({ handleChange, isLegacyFlow, platform, priority }: Props) {
  const [inputValue, setInputValue] = useState("");

  const [defaultEmails, setDefaultEmails] = useState<string[]>([]);

  const defaultAccountManagerEmails = useAccountManagerEmails(platform, priority);
  const measureTextWidth = useMeasureTextWidth(14);

  useEffect(() => {
    setDefaultEmails(defaultAccountManagerEmails);
  }, [defaultAccountManagerEmails]);

  const [emails, setEmails] = useState<string[]>([]);

  const allEmails = useMemo(() => [...defaultEmails, ...emails], [defaultEmails, emails]);

  useEffect(() => {
    handleChange(allEmails);
  }, [allEmails, handleChange]);

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("services");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", ",", " ", "Tab"].includes(e.key)) {
      const trimmedValue = inputValue.trim().replace(/,$/, "");
      if (trimmedValue && isEmailValid(trimmedValue)) {
        e.preventDefault();
        if (!emails.includes(trimmedValue)) {
          setEmails([...emails, trimmedValue]);
        }
        setInputValue("");
      }
    } else if ((e.key === "Backspace" || e.key === "Delete") && inputValue === "" && emails.length > 0) {
      e.preventDefault();
      handleChange(emails.slice(0, -1));
    }
  };

  const handleDelete = (emailToDelete: string) => {
    if (defaultEmails.includes(emailToDelete)) {
      setDefaultEmails(defaultEmails.filter((email) => email !== emailToDelete));
    } else {
      setEmails(emails.filter((email) => email !== emailToDelete));
    }
  };

  const handleBoxClick = () => {
    inputRef.current?.focus();
  };

  const formatChipLabel = (email: string) => (email.length > 25 ? `${email.substring(0, 25)}...` : email);

  // Calculate input width based on content
  const getInputWidth = () => (inputValue ? `${measureTextWidth(inputValue)}px` : "30px");

  return (
    <Box sx={{ position: "relative" }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          position: "absolute",
          top: -10,
          left: 10,
          px: 0.5,
          bgcolor: isLegacyFlow ? "background.paper" : "general.backgroundDark",
          zIndex: 1,
        }}
      >
        {t("DetailsStep.additionalCCs")}
      </Typography>
      <Box
        sx={{
          pt: 1.5,
          pb: 1,
          px: 2,
          border: "1px solid",
          borderColor: "general.outlineBorder",
          borderRadius: 1,
          position: "relative",
          cursor: "text",
          "&:hover": {
            borderColor: "text.primary",
          },
        }}
        onClick={handleBoxClick}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          {allEmails.map((email) => (
            <Tooltip title={email} key={email}>
              <Chip
                label={formatChipLabel(email)}
                size="small"
                sx={{ zIndex: 10, my: 0.6 }}
                onDelete={() => {
                  handleDelete(email);
                }}
                data-cy="email-chip-item"
              />
            </Tooltip>
          ))}
          <TextField
            variant="standard"
            inputRef={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                disableUnderline: true,
                sx: { mt: 0.5 },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              width: getInputWidth(),
            }}
            data-cy="email-input-field"
          />
        </Box>
      </Box>
    </Box>
  );
}

export default EmailChipsInput;
